import { useCallback, useEffect, useState } from 'react';
import { downloadAdminEventSubscribersCsv, fetchAdminEventSubscribers } from '../infrastructure/adminApi';

const PAGE_SIZE = 100;

export default function EventSubscribersPanel({ authorization, onSessionExpired }) {
  const [data, setData] = useState({ total: 0, items: [] });
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async (nextOffset) => {
    setIsLoading(true);
    setError('');
    try {
      const result = await fetchAdminEventSubscribers(authorization, { limit: PAGE_SIZE, offset: nextOffset });
      setData(result);
      setOffset(result.offset || nextOffset);
    } catch (requestError) {
      if (requestError.status === 401) {
        onSessionExpired?.();
      } else {
        setError(requestError.message);
      }
    } finally {
      setIsLoading(false);
    }
  }, [authorization, onSessionExpired]);

  useEffect(() => {
    load(0);
  }, [load]);

  const total = data.total || 0;
  const items = data.items || [];

  const downloadCsv = async () => {
    setError('');
    try {
      await downloadAdminEventSubscribersCsv(authorization);
    } catch (requestError) {
      if (requestError.status === 401) onSessionExpired?.();
      else setError(`No se pudo descargar el CSV. ${requestError.message}`);
    }
  };

  return (
    <section className="mx-auto max-w-[1480px] px-5 py-8 lg:px-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[.2em] text-[#9d8cab]">Opt-in del inicio</p>
          <h2 className="font-display text-3xl uppercase text-white">Eventos en Toluca</h2>
          <p className="mt-2 max-w-xl text-sm leading-6 text-[#b7a9c4]">
            Correos guardados desde “¿Te gustaría recibir información de nuestros eventos en Toluca?”.
            Tabla <span className="font-mono">event_subscribers</span>.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <p className="font-mono text-xs text-[#d6c9df]" aria-live="polite">{total} suscritos</p>
          <button
            type="button"
            onClick={downloadCsv}
            disabled={total === 0}
            title="Correo, usuario, etiqueta, origen y fecha. Importable en Mailchimp y newsletters de WordPress."
            className="min-h-[44px] border border-[#F7E04B] px-4 py-2 text-[10px] font-black uppercase tracking-wider text-[#F7E04B] disabled:opacity-40"
          >
            Descargar CSV
          </button>
          <button
            type="button"
            onClick={() => load(offset)}
            disabled={isLoading}
            className="min-h-[44px] border border-[#594a65] px-4 py-2 text-[10px] font-black uppercase tracking-wider text-[#d6c9df] disabled:opacity-60"
          >
            {isLoading ? 'Cargando…' : 'Actualizar'}
          </button>
        </div>
      </div>

      {error && <p className="mt-4 border border-[#F7E04B]/40 bg-[#2b1219] p-3 text-sm text-[#ffd8de]" role="alert">{error}</p>}

      <div className="mt-6 overflow-x-auto border border-[#312a3a] bg-[#0d0b11]">
        <table className="w-full min-w-[560px] text-left text-sm">
          <thead>
            <tr className="border-b border-[#312a3a] text-[10px] uppercase tracking-[.16em] text-[#9d8cab]">
              <th className="px-4 py-3">Correo</th>
              <th className="px-4 py-3">Origen</th>
              <th className="px-4 py-3">Registro</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-b border-[#1d1826] text-[#eee8f5]">
                <td className="break-all px-4 py-3 font-bold">{item.email}</td>
                <td className="px-4 py-3 font-mono text-xs text-[#b7a9c4]">{item.source}</td>
                <td className="whitespace-nowrap px-4 py-3 text-xs text-[#b7a9c4]">
                  {new Date(item.created_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!isLoading && items.length === 0 && !error && (
          <p className="px-4 py-10 text-center text-sm text-[#8f829c]">Aún no hay suscripciones. Aparecen aquí en cuanto alguien deje su correo en el inicio.</p>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => load(Math.max(offset - PAGE_SIZE, 0))}
          disabled={isLoading || offset === 0}
          className="min-h-[44px] border border-[#3a3344] px-4 py-2 text-[10px] font-black uppercase tracking-wider text-[#a99ab4] disabled:opacity-40"
        >
          ← Anteriores
        </button>
        <p className="font-mono text-[11px] text-[#8f829c]">
          {total === 0 ? '0 de 0' : `${offset + 1}–${offset + items.length} de ${total}`}
        </p>
        <button
          type="button"
          onClick={() => load(offset + PAGE_SIZE)}
          disabled={isLoading || offset + items.length >= total}
          className="min-h-[44px] border border-[#3a3344] px-4 py-2 text-[10px] font-black uppercase tracking-wider text-[#a99ab4] disabled:opacity-40"
        >
          Siguientes →
        </button>
      </div>
    </section>
  );
}
